import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { Subject, filter, fromEvent, map, takeUntil, zip } from 'rxjs';

/**
 * This directive is used to detect swipe events on a dom element.
 * It will emit a new index based on the swipe direction.
 */
@Directive({
  selector: '[ssWwwSwipe]',
  standalone: true,
})
export class SwipeDirective implements AfterViewInit, OnDestroy {
  private el = inject(ElementRef);

  @Input({ required: true }) index!: number;
  @Input({ required: true }) length!: number;
  @Output() nextIndex = new EventEmitter<number>();
  destroy$ = new Subject<void>();

  ngAfterViewInit(): void {
    // https://www.learnrxjs.io/learn-rxjs/operators/combination/zip
    // pairs up values from multiple observables
    zip(
      fromEvent<TouchEvent>(this.el.nativeElement, 'touchstart'),
      fromEvent<TouchEvent>(this.el.nativeElement, 'touchend')
    )
      .pipe(
        // Only emit when there is one touch point on each event
        filter(
          (e) => e[0].touches.length === 1 && e[1].changedTouches.length === 1
        ),
        // map touchstart and touchend events to their clientX position
        map((e) => ({
          start: e[0].touches[0].clientX,
          end: e[1].changedTouches[0].clientX,
        })),
        // // make sure the difference between the start and end is at least 30px
        filter(({ start, end }) => {
          const difference = end - start;
          return Math.abs(difference) > 30;
        }),
        // map the difference to a increment or decrement
        map(({ start, end }) => (start > end ? 1 : -1)),
        takeUntil(this.destroy$)
      )
      .subscribe((plusOrMinusOne) => {
        let nextIndex = this.index + plusOrMinusOne;
        if (nextIndex < 0) {
          nextIndex = this.length - 1;
        }
        if (nextIndex >= this.length) {
          nextIndex = 0;
        }
        this.nextIndex.emit(nextIndex);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
